import type { ClientInput } from '../housien-models/ClientInput';
import type { Client } from '../housien-models/Client';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';

const CLIENT_NOT_FOUND = 'Client not found';

/**
 * Creates a client via `POST /agency/{agencyId}/clients`.
 */
export function postAgencyClient(
  agencyId: string,
  requestBody: ClientInput,
): CancelablePromise<Client> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/clients',
    path: { agencyId },
    body: requestBody,
    mediaType: 'application/json',
    errors: { 400: 'Invalid input data' },
  });
}

interface AgencyClientsFilters {
  type?: string;
  search?: string;
  role?: string;
  broker?: string;
  originChannel?: string;
  withoutReturn?: string;
  importId?: string;
  /** Monthly-income window, in BRL. */
  incomeMin?: string;
  incomeMax?: string;
  /** Where the client LIVES. */
  city?: string;
  neighborhood?: string;
  state?: string;
  /** What the client is LOOKING FOR -- read from the demand profile. */
  prefCity?: string;
  prefNeighborhood?: string;
  prefPriceMin?: string;
  prefPriceMax?: string;
  prefBusinessType?: string;
  prefPropertyKind?: string;
  prefBedroomsMin?: string;
  prefPetFriendly?: string;
  prefFurnished?: string;
  /** Listing order: `asc` walks from the oldest; absent means newest first. */
  order?: string;
}

/**
 * Maps the filter object onto the `GET /agency/{agencyId}/clients` query params,
 * omitting unset fields.
 */
function buildAgencyClientsQuery(filters?: AgencyClientsFilters): Record<string, string> {
  const fieldToParam: Record<keyof AgencyClientsFilters, string> = {
    type: 'type',
    search: 'search',
    role: 'role',
    broker: 'broker',
    originChannel: 'origin_channel',
    withoutReturn: 'without_return',
    importId: 'import_id',
    incomeMin: 'income_min',
    incomeMax: 'income_max',
    city: 'city',
    neighborhood: 'neighborhood',
    state: 'state',
    prefCity: 'pref_city',
    prefNeighborhood: 'pref_neighborhood',
    prefPriceMin: 'pref_price_min',
    prefPriceMax: 'pref_price_max',
    prefBusinessType: 'pref_business_type',
    prefPropertyKind: 'pref_property_kind',
    prefBedroomsMin: 'pref_bedrooms_min',
    prefPetFriendly: 'pref_pet_friendly',
    prefFurnished: 'pref_furnished',
    order: 'order',
  };
  const query: Record<string, string> = {};
  for (const [field, param] of Object.entries(fieldToParam) as [keyof AgencyClientsFilters, string][]) {
    const value = filters?.[field];
    if (value) query[param] = value;
  }
  return query;
}

/**
 * Lists clients with pagination and filters via `GET /agency/{agencyId}/clients`.
 */
export function getAgencyClients(
  agencyId: string,
  page: number = 1,
  limit: number = 10,
  filters?: AgencyClientsFilters,
): CancelablePromise<{ data: Array<Client> }> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/clients',
    path: { agencyId },
    query: {
      _page: page,
      _limit: limit,
      ...buildAgencyClientsQuery(filters),
    },
  });
}

/**
 * Gets a single client via `GET /agency/{agencyId}/clients/{clientId}`.
 */
export function getAgencyClient(
  agencyId: string,
  clientId: string,
): CancelablePromise<Client> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/clients/{client_id}',
    path: { agencyId, client_id: clientId },
    errors: { 404: CLIENT_NOT_FOUND },
  });
}

export interface ClientAttendanceNotice {
  /** Whether another broker holds an open attendance for this client. */
  has_open_attendance: boolean;
  /** How many open attendances other brokers hold. */
  count: number;
  /** The e-mails of the brokers holding them. */
  brokers: string[];
  /**
   * How many each of those brokers holds.
   *
   * The total alone cannot be read: "3 abertos com Jorge, Lucas" leaves
   * the reader unable to tell one colleague holding three from three
   * colleagues holding one each.
   */
  broker_counts?: Record<string, number>;
}

/**
 * Reads whether another broker already attends this client, via
 * `GET /agency/{agencyId}/clients/{clientId}/attendance-status`.
 *
 * The client book is shared across the agency but the attendance board
 * is not, so `GET /deals` hides a colleague's attendance from a broker
 * and the client's file reads as free. This reports the collision only.
 */
export function getAgencyClientAttendanceStatus(
  agencyId: string,
  clientId: string,
): CancelablePromise<ClientAttendanceNotice> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/clients/{client_id}/attendance-status',
    path: { agencyId, client_id: clientId },
  });
}

/**
 * Updates a client via `PUT /agency/{agencyId}/clients/{clientId}`.
 */
export function putAgencyClient(
  agencyId: string,
  clientId: string,
  requestBody: ClientInput,
): CancelablePromise<Client> {
  return __request(OpenAPIHousien, {
    method: 'PUT',
    url: '/agency/{agencyId}/clients/{client_id}',
    path: { agencyId, client_id: clientId },
    body: requestBody,
    mediaType: 'application/json',
    errors: { 404: CLIENT_NOT_FOUND },
  });
}

/**
 * Deletes a client via `DELETE /agency/{agencyId}/clients/{clientId}`.
 */
export function deleteAgencyClient(
  agencyId: string,
  clientId: string,
): CancelablePromise<void> {
  return __request(OpenAPIHousien, {
    method: 'DELETE',
    url: '/agency/{agencyId}/clients/{client_id}',
    path: { agencyId, client_id: clientId },
    errors: { 404: CLIENT_NOT_FOUND },
  });
}

/**
 * Unified ClientsService with all client CRUD operations.
 */
export class ClientsService {
  public static postAgencyClient = postAgencyClient;
  public static getAgencyClients = getAgencyClients;
  public static getAgencyClient = getAgencyClient;
  public static getAgencyClientAttendanceStatus = getAgencyClientAttendanceStatus;
  public static putAgencyClient = putAgencyClient;
  public static deleteAgencyClient = deleteAgencyClient;
}
